// autopilot-status-command.ts
//
// `avatar autopilot status` — tách khỏi autopilot.ts (review 03a L6: file mẹ
// 198/200 dòng, 03b thêm `--decisions`). Read-only: không lock, không ghi
// journal; chỉ đọc projection/control root và ghi bản HTML status.
// `--decisions` (V2-1): sổ quyết định dựng từ journal (decision-ledger-projection.ts).
import { join } from "node:path";
import type { Command } from "commander";
import { readDecisionLedger } from "../lib/autopilot/decision-ledger-projection.js";
import { decisionLedgerToSnakeCaseJson } from "../lib/autopilot/decision-ledger-snake-case-json.js";
import { renderDecisionLedgerText } from "../lib/autopilot/decision-ledger-status-text.js";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { missionTestEnvWarningForReadOnlyCommand } from "../lib/autopilot/mission-secrets-test-env-run-preflight.js";
import { claudeBinOverrideNotice } from "../lib/autopilot/run-command-session-deps.js";
import { readStatusChatDriveFields } from "../lib/autopilot/status-chat-drive-fields.js";
import {
  formatMissionStatusLine,
  renderStatusTableText,
} from "../lib/autopilot/status-operating-table.js";
import {
  readStatusSnapshot,
  watchStatus,
  writeStatusHtml,
} from "../lib/autopilot/status-renderer.js";
import {
  STATUS_WAIT_MAX_SEC,
  waitForEngineChange,
} from "../lib/autopilot/status-supervisor-liveness.js";
import {
  writeMachineReadableError,
  writeMachineReadableJson,
} from "../lib/machine-readable-json-output-contract.js";
import { log, setMachineReadableMode } from "../lib/terminal-logger.js";

export interface RegisterAutopilotStatusCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  /** 06k C10: lock máy để báo `supervisor_alive`; nhịp poll `--wait` tiêm được cho test. */
  machineLockPath: string;
  waitPollMs?: number;
}

interface StatusOptions {
  mission?: string;
  repo?: string;
  watch?: boolean;
  json?: boolean;
  decisions?: boolean;
  wait?: string;
}

/** 06k C10: `--wait` hợp lệ ⇒ số giây; sai/xung đột ⇒ thông điệp lỗi (exit 1). */
function parseWait(opts: StatusOptions): { sec?: number; error?: string } {
  if (opts.wait === undefined) return {};
  const sec = Number(opts.wait);
  if (!Number.isInteger(sec) || sec < 1 || sec > STATUS_WAIT_MAX_SEC) {
    return { error: `--wait phải là số nguyên 1..${STATUS_WAIT_MAX_SEC} (giây).` };
  }
  if (opts.watch || opts.decisions) return { error: "--wait không dùng cùng --watch/--decisions." };
  return { sec };
}

export function registerAutopilotStatusCommand(
  autopilot: Command,
  deps: RegisterAutopilotStatusCommandDeps,
): void {
  autopilot
    .command("status")
    .description("Đọc trạng thái mission (read-only)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--watch", "Refresh định kỳ (5s)")
    .option("--json", "Output JSON cho máy đọc")
    .option("--decisions", "Sổ quyết định (auto + người), worker ghi nhận, câu đang chờ")
    .option("--wait <sec>", "Chờ tới khi supervisor dừng/sống lại (≤ 540 s) rồi in một snapshot")
    .action(async (opts: StatusOptions) => {
      if (opts.json) setMachineReadableMode(true); // H18: stdout carries the JSON only
      const wait = parseWait(opts);
      if (wait.error !== undefined) {
        if (opts.json) writeMachineReadableError("autopilot status", wait.error);
        else process.stderr.write(`✗ ${wait.error}\n`);
        process.exitCode = 1;
        return;
      }
      // Review 03b L5: the ledger is read once — never drop --watch silently.
      if (opts.decisions && opts.watch) {
        const message =
          "--decisions không dùng cùng --watch (sổ đọc một lần từ journal) — bỏ --watch.";
        if (opts.json) writeMachineReadableError("autopilot status", message);
        else process.stderr.write(`✗ ${message}\n`);
        process.exit(1);
      }
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      const resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
      // Phase 04a N33: `test.env` sai quyền ⇒ cảnh báo (stderr khi --json), vẫn render.
      const secretsWarning = await missionTestEnvWarningForReadOnlyCommand(resolved.missionId);
      if (secretsWarning) {
        if (opts.json) process.stderr.write(`⚠ ${secretsWarning}\n`);
        else log.warn(secretsWarning);
      }
      // V2-1 §4.1: the ledger is a journal projection — no snapshot/HTML, no watch loop.
      if (opts.decisions) {
        const ledger = await readDecisionLedger(resolved.controlRoot);
        if (opts.json) {
          writeMachineReadableJson({
            command: "autopilot status --decisions",
            mission_id: resolved.missionId,
            ledger: decisionLedgerToSnakeCaseJson(ledger), // M3: snake_case like status --json
          });
        } else log.info(renderDecisionLedgerText(ledger, resolved.missionId));
        return;
      }
      // Operating table (P13 §15) needs the execution repo's `.avatar/tickets/*.json`
      // — same path convention as `doctor`/`run` (`<mission_home>/workspace`).
      const missionWorkspace = join(resolved.missionHome, "workspace");

      if (opts.watch) {
        // Same intervalMs on every refresh (spec §15) — watchStatus's default
        // (5s) is passed once and reused by its own polling loop, never
        // recomputed per tick.
        watchStatus(
          resolved.controlRoot,
          {
            onSnapshot: async (payload) => {
              await writeStatusHtml(workspaceRoot, payload);
              if (opts.json) writeMachineReadableJson({ command: "autopilot status", payload });
              else if ("tickets_detail" in payload) {
                log.info(`[${new Date().toISOString()}]`);
                log.info(renderStatusTableText(payload.tickets_detail, payload.mission_summary));
              } else {
                const line = formatMissionStatusLine(payload.mission_id, payload.status);
                log.info(`[${new Date().toISOString()}] ${line}`);
              }
            },
          },
          missionWorkspace,
          resolved.missionId,
        );
        return;
      }

      const watched = { ...resolved, machineLockPath: deps.machineLockPath };
      const timedOut =
        wait.sec === undefined
          ? undefined
          : await waitForEngineChange({ ...watched, waitSec: wait.sec, pollMs: deps.waitPollMs });
      const payload = await readStatusSnapshot(
        resolved.controlRoot,
        missionWorkspace,
        resolved.missionId,
      );
      await writeStatusHtml(workspaceRoot, payload);
      const binNotice = claudeBinOverrideNotice(); // never silent about a test-only claude binary override
      if (binNotice) log.warn(binNotice);
      if (opts.json) {
        const chatDrive = await readStatusChatDriveFields(watched);
        const extra = timedOut === undefined ? {} : { wait_timed_out: timedOut };
        writeMachineReadableJson({
          command: "autopilot status",
          payload: { ...payload, ...chatDrive, ...extra },
        });
      } else if ("tickets_detail" in payload) {
        log.info(renderStatusTableText(payload.tickets_detail, payload.mission_summary));
      } else {
        log.info(formatMissionStatusLine(payload.mission_id, payload.status));
      }
    });
}
